import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiShoppingCart, FiHeart } from 'react-icons/fi';
import { Product } from '../types';
import { useCart } from '../context/CartContext';

const Card = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 18px;
`;

const ActionButtons = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${Card}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: #e74c3c;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const BrandName = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0 0 0.5rem 0;
  font-weight: 500;
`;

const ProductTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const Price = styled.span`
  font-size: 18px;
  font-weight: bold;
  color: #1a1a1a;
`;

const OriginalPrice = styled.span`
  font-size: 14px;
  color: #999;
  text-decoration: line-through;
`;

const Currency = styled.span`
  font-size: 14px;
  color: #666;
  margin-left: 2px;
`;

const AddToCartButton = styled.button`
  width: 100%;
  padding: 12px;
  background: #1a1a1a;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const StockInfo = styled.p`
  font-size: 12px;
  color: ${props => props.color || '#666'};
  margin: 0.5rem 0 0 0;
`;

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const category = product.category?.toLocaleLowerCase('tr-TR') || '';
    const requiresSize = !['aksesuar', 'accessory', 'saat', 'watch', 'ayakkabı', 'shoe']
      .some(token => category.includes(token));
    if (requiresSize) {
      navigate(`/product/${product.id}`);
      return;
    }
    addToCart(product, 1);
  };

  const getStockStatus = () => {
    const stock = product.stockQuantity || 0;
    if (stock === 0) return { text: 'Stokta Yok', color: '#e74c3c' };
    if (stock < 5) return { text: `Son ${stock} Adet`, color: '#f39c12' };
    return { text: 'Stokta Var', color: '#27ae60' };
  };

  const stockStatus = getStockStatus();
  const hasDiscount = false; // Bu örnekte indirim hesaplaması yok
  const imageUrl = product.images && product.images.length > 0 ? product.images[0] : null;

  return (
    <Card>
      <Link to={`/product/${product.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
        <ImageContainer>
          {imageUrl ? (
            <ProductImage src={imageUrl} alt={product.title} />
          ) : (
            <ImagePlaceholder>
              Resim Yok
            </ImagePlaceholder>
          )}
          
          {hasDiscount && <Badge>İndirim</Badge>}
          
          <ActionButtons>
            <ActionButton title="Favorilere Ekle">
              <FiHeart size={18} />
            </ActionButton>
          </ActionButtons>
        </ImageContainer>

        <CardContent>
          {product.brand && (
            <BrandName>{product.brand.name}</BrandName>
          )}
          
          <ProductTitle>{product.title}</ProductTitle>
          
          <PriceContainer>
            <Price>
              {product.price ? product.price.toLocaleString('tr-TR') : 'Fiyat Belirtilmemiş'}
              {product.price && <Currency>{product.currency || 'TRY'}</Currency>}
            </Price>
            {hasDiscount && (
              <OriginalPrice>
                {product.price && (product.price * 1.2).toLocaleString('tr-TR')} {product.currency || 'TRY'}
              </OriginalPrice>
            )}
          </PriceContainer>

          <StockInfo color={stockStatus.color}>
            {stockStatus.text}
          </StockInfo>
        </CardContent>
      </Link>
      
      <div style={{ padding: '0 1.5rem 1.5rem' }}>
        <AddToCartButton 
          onClick={handleAddToCart}
        >
          <FiShoppingCart size={18} />
          Sepete Ekle
        </AddToCartButton>
      </div>
    </Card>
  );
};

export default ProductCard;
